import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { supabase } from '../../lib/supabase';
import Icon from '../AppIcon';
import RichiestaFerieModal from '../RichiestaFerieModal';

const PAGE_SIZE = 10;

const MojGodisnji = ({ userId, refreshTrigger }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [richieste, setRichieste] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');

  const [detailsRow, setDetailsRow] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const MESI_LABEL = {
    '01': t('ferie.months.01'), '02': t('ferie.months.02'), '03': t('ferie.months.03'), '04': t('ferie.months.04'),
    '05': t('ferie.months.05'), '06': t('ferie.months.06'), '07': t('ferie.months.07'), '08': t('ferie.months.08'),
    '09': t('ferie.months.09'), '10': t('ferie.months.10'), '11': t('ferie.months.11'), '12': t('ferie.months.12'),
  };

  // Date reali iz baza (sve datume 'dal' za tog korisnika) — koriste se za popunjavanje filtera
  const [allDates, setAllDates] = useState([]);

  useEffect(() => {
    const fetchAllDates = async () => {
      if (!userId) return;
      const { data, error } = await supabase
        .from('ferie')
        .select('dal')
        .eq('id_utente', userId)
        .order('dal', { ascending: false });

      if (!error && data) setAllDates(data.map(r => r.dal).filter(Boolean));
    };
    fetchAllDates();
  }, [userId, refreshTrigger]);

  const availableYears = useMemo(() => {
    const years = new Set(allDates.map(d => d.substring(0, 4)));
    return Array.from(years).sort((a, b) => b.localeCompare(a));
  }, [allDates]);

  const availableMonths = useMemo(() => {
    if (selectedYear === 'all') return [];
    const months = new Set(
      allDates
        .filter(d => d.startsWith(selectedYear))
        .map(d => d.substring(5, 7))
    );
    return Array.from(months).sort();
  }, [allDates, selectedYear]);

  const buildQuery = (pageIndex) => {
    let query = supabase
      .from('ferie')
      .select('*')
      .eq('id_utente', userId)
      .order('data_richiesta', { ascending: false });

    if (selectedYear !== 'all') {
      const startDate = `${selectedYear}-01-01`;
      const endDate = `${selectedYear}-12-31`;
      query = query.gte('dal', startDate).lte('dal', endDate);
    }

    if (selectedMonth !== 'all' && selectedYear !== 'all') {
      const startDate = `${selectedYear}-${selectedMonth}-01`;
      const lastDay = new Date(selectedYear, parseInt(selectedMonth), 0).getDate();
      const endDate = `${selectedYear}-${selectedMonth}-${lastDay}`;
      query = query.gte('dal', startDate).lte('dal', endDate);
    }

    const from = pageIndex * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;
    return query.range(from, to);
  };

  const fetchRichieste = async (reset = true) => {
    if (!userId) return;
    if (reset) {
      setLoading(true);
      setPage(0);
    }
    const { data, error } = await buildQuery(0);
    if (!error && data) {
      setRichieste(data);
      setHasMore(data.length === PAGE_SIZE);
    }
    setLoading(false);
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    const nextPage = page + 1;
    const { data, error } = await buildQuery(nextPage);
    if (!error && data) {
      setRichieste(prev => [...prev, ...data]);
      setHasMore(data.length === PAGE_SIZE);
      setPage(nextPage);
    }
    setLoadingMore(false);
  };

  useEffect(() => {
    fetchRichieste(true);
  }, [userId, refreshTrigger, selectedYear, selectedMonth]);

  const handleDelete = async () => {
    if (!detailsRow) return;
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('ferie')
        .delete()
        .eq('id', detailsRow.id);

      if (error) throw error;

      setRichieste(prev => prev.filter(r => r.id !== detailsRow.id));
      setConfirmDelete(false);
      setDetailsRow(null);
    } catch (err) {
      console.error('Errore eliminazione:', err);
    }
    setDeleting(false);
  };

  const closeModal = () => {
    setDetailsRow(null);
    setConfirmDelete(false);
  };

  const StatusBadge = ({ accettato }) => {
    if (accettato === true) {
      return (
        <span className="px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 text-[9px] font-black uppercase tracking-widest border border-emerald-500/20">
          {t('ferie.status_accepted')}
        </span>
      );
    }
    if (accettato === false) {
      return (
        <span className="px-3 py-1 rounded-full bg-destructive/10 text-destructive text-[9px] font-black uppercase tracking-widest border border-destructive/20">
          {t('ferie.status_rejected')}
        </span>
      );
    }
    return (
      <span className="px-3 py-1 rounded-full bg-orange-500/10 text-orange-500 text-[9px] font-black uppercase tracking-widest border border-orange-500/20">
        {t('ferie.status_pending')}
      </span>
    );
  };

  const formatTipo = (tipo) => {
    if (tipo === 'giorno_singolo') return t('ferie.type_single_day');
    if (tipo === 'piu_giorni') return t('ferie.type_multi_day');
    if (tipo === 'ore') return t('ferie.type_hours');
    return tipo;
  };

  const formatTurno = (turno) => {
    const map = {
      'Giornaliero': t('ferie.shift_daily'),
      'Primo Turno': t('ferie.shift_first'),
      'Secondo Turno': t('ferie.shift_second'),
      'Terzo Turno': t('ferie.shift_third'),
      'Altro': t('ferie.shift_other'),
    };
    return map[turno] || turno;
  };

  const formatDateTime = (dt) => {
    if (!dt) return '—';
    const locale = i18n.language === 'bs' ? 'bs-BA' : i18n.language === 'it' ? 'it-IT' : i18n.language === 'de' ? 'de-DE' : 'en-GB';
    return new Date(dt).toLocaleString(locale, {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="mt-10 animate-in fade-in slide-in-from-bottom-4 duration-700">

      <div className="flex justify-between items-center mb-6">
        <h3 className="text-sm font-black uppercase tracking-[0.2em] text-foreground/70">
          {t('ferie.my_requests_title')}
        </h3>
      </div>

      {/* FILTRI */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <select
          value={selectedYear}
          onChange={(e) => { setSelectedYear(e.target.value); setSelectedMonth('all'); }}
          className="bg-card border border-border/60 rounded-lg px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-foreground outline-none focus:ring-4 focus:ring-primary/10 cursor-pointer"
        >
          <option value="all">{t('ferie.all_years')}</option>
          {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          disabled={selectedYear === 'all' || availableMonths.length === 0}
          className="bg-card border border-border/60 rounded-lg px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-foreground outline-none focus:ring-4 focus:ring-primary/10 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          <option value="all">{t('ferie.all_months')}</option>
          {availableMonths.map(m => (
            <option key={m} value={m}>{MESI_LABEL[m]}</option>
          ))}
        </select>

        {(selectedYear !== 'all' || selectedMonth !== 'all') && (
          <button
            onClick={() => { setSelectedYear('all'); setSelectedMonth('all'); }}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide text-muted-foreground hover:text-primary transition-colors"
          >
            <Icon name="X" size={12} /> {t('ferie.reset_btn')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Icon name="Loader2" size={24} className="animate-spin text-primary/50" />
        </div>
      ) : richieste.length === 0 ? (
        <div className="bg-card border border-border/60 rounded-3xl p-10 text-center">
          <Icon name="CalendarOff" size={36} className="mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-sm font-bold text-muted-foreground uppercase tracking-wide">
            {t('ferie.no_requests')}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {richieste.map((r) => (
              <div
                key={r.id}
                className="bg-card border border-border/60 rounded-2xl p-5 flex items-center justify-between gap-4 hover:border-primary/30 transition-all"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="p-3 bg-primary/10 text-primary rounded-xl shrink-0">
                    <Icon name={r.tipo_richiesta === 'ore' ? 'Clock' : 'Calendar'} size={20} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-black text-foreground truncate">
                      {formatTipo(r.tipo_richiesta)} — {r.dal}{r.al && r.al !== r.dal ? ` → ${r.al}` : ''}
                    </p>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide truncate">
                      {formatTurno(r.turno)} {r.giorni > 0 ? `· ${r.giorni} ${t('ferie.days_label')}` : ''} {r.ore_dal ? `· ${r.ore_dal} - ${r.ore_al}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusBadge accettato={r.accettato} />
                  <button
                    onClick={() => setDetailsRow(r)}
                    className="p-2.5 text-muted-foreground/40 hover:text-primary hover:bg-primary/10 rounded-xl transition-all"
                    title={t('ferie.details_tooltip')}
                  >
                    <Icon name="Eye" size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {hasMore && (
            <div className="flex justify-center mt-6">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-muted/40 text-foreground font-black uppercase text-[10px] tracking-widest hover:bg-muted/70 transition-all disabled:opacity-50"
              >
                {loadingMore ? (
                  <div className="w-4 h-4 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Icon name="ChevronDown" size={14} /> {t('ferie.load_more_btn')}
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}

      {/* MODAL DETALJI */}
      {detailsRow && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-[3px] px-4 animate-in fade-in duration-200"
          onClick={closeModal}
        >
          <div
            className="w-full max-w-md bg-card rounded-[2rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-border/50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative p-6 pb-5 border-b border-border/60 bg-gradient-to-b from-muted/30 to-transparent">
              <button
                onClick={closeModal}
                className="absolute top-5 right-5 p-2 hover:bg-muted rounded-xl transition-colors text-muted-foreground"
              >
                <Icon name="X" size={18} />
              </button>
              <div className="flex items-center gap-3">
                <div className="p-3 bg-primary/10 text-primary rounded-2xl">
                  <Icon name={detailsRow.tipo_richiesta === 'ore' ? 'Clock' : 'Calendar'} size={22} />
                </div>
                <div>
                  <h2 className="font-black uppercase text-[12px] tracking-widest text-foreground">
                    {t('ferie.details_title')}
                  </h2>
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide mt-0.5">
                    {formatTipo(detailsRow.tipo_richiesta)}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="flex justify-center mb-5">
                <StatusBadge accettato={detailsRow.accettato} />
              </div>

              <div className="space-y-1">
                {[
                  { label: t('ferie.from_label'), value: detailsRow.dal || '—', icon: 'Calendar' },
                  { label: t('ferie.to_label'), value: detailsRow.al || '—', icon: 'Calendar' },
                  ...(detailsRow.ore_dal ? [{ label: t('ferie.time_label'), value: `${detailsRow.ore_dal} - ${detailsRow.ore_al}`, icon: 'Clock' }] : []),
                  { label: t('ferie.days_column_label'), value: detailsRow.giorni > 0 ? detailsRow.giorni : '—', icon: 'Hash' },
                  { label: t('ferie.shift_label'), value: formatTurno(detailsRow.turno) || '—', icon: 'Sunrise' },
                  { label: t('ferie.weekend_included'), value: detailsRow.weekend_incluso ? t('ferie.yes') : t('ferie.no'), icon: 'CalendarDays' },
                  { label: t('ferie.note_label'), value: detailsRow.note || '—', icon: 'StickyNote' },
                ].map(({ label, value, icon }) => (
                  <div key={label} className="flex justify-between items-center py-2.5 border-b border-border/30">
                    <span className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                      <Icon name={icon} size={12} className="text-muted-foreground/50" />
                      {label}
                    </span>
                    <span className="text-[12px] font-bold text-foreground text-right max-w-[55%] truncate">
                      {value}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-4 rounded-2xl bg-muted/30 border border-border/40 p-4 space-y-2.5">
                <p className="text-[9px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 mb-1">
                  {t('ferie.timeline_title')}
                </p>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-bold text-muted-foreground">{t('ferie.timeline_sent')}</span>
                  <span className="text-[11px] font-black text-foreground">{formatDateTime(detailsRow.data_richiesta)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-bold text-muted-foreground">{t('ferie.timeline_answered_by')}</span>
                  <span className="text-[11px] font-black text-foreground">{detailsRow.risposto_da || '—'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-bold text-muted-foreground">{t('ferie.timeline_answer_date')}</span>
                  <span className="text-[11px] font-black text-foreground">
                    {detailsRow.data_risposta ? formatDateTime(detailsRow.data_risposta) : t('ferie.status_pending')}
                  </span>
                </div>
              </div>

              {/* DELETE FOOTER — samo ako je Na Čekanju (null) */}
              {detailsRow.accettato === null && (
                <div className="mt-6 pt-5 border-t border-border/40">
                  {!confirmDelete ? (
                    <button
                      onClick={() => setConfirmDelete(true)}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-destructive/10 text-destructive font-black uppercase text-[10px] tracking-widest hover:bg-destructive/20 transition-all"
                    >
                      <Icon name="Trash2" size={15} />
                      {t('ferie.delete_btn')}
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-[10px] font-bold text-muted-foreground text-center uppercase tracking-wide">
                        {t('ferie.delete_confirm_text')}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setConfirmDelete(false)}
                          disabled={deleting}
                          className="flex-1 py-3 rounded-2xl bg-muted text-muted-foreground font-black uppercase text-[10px] tracking-widest hover:text-foreground transition-colors disabled:opacity-50"
                        >
                          {t('ferie.cancel_btn')}
                        </button>
                        <button
                          onClick={handleDelete}
                          disabled={deleting}
                          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-destructive text-white font-black uppercase text-[10px] tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50"
                        >
                          {deleting ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          ) : (
                            <>
                              <Icon name="Trash2" size={14} />
                              {t('ferie.confirm_delete_btn')}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <RichiestaFerieModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        userId={userId}
        onSuccess={() => fetchRichieste(true)}
      />
    </div>
  );
};

export default MojGodisnji;